/**
 * Soft≠Live Page167 — Control Security residual liquid (page131/87 + status + e-stop Live).
 * Mount: /control/security
 * Buses:
 *   GET /sa/security/status (always-on Live posture)
 *   GET /sa/security/mac-drift · GET /admin/security/audit
 *   POST /admin/security/rotate-cmd-key
 *   Live physical-inhibit status/engage/clear (resource-fabric product flag)
 * Soft residual deleted:
 *  - bare Refresh status CTA
 *  - Soft “rotated” / Soft “stopped” invent without Live POST
 *  - Soft e-stop success when physical_inhibit unmounted
 *  - sandpit cards without liquid rail / KPI / posture SoT
 */
import { useMemo, useState } from "react";
import { html, _F, Ico, KV, Card, Field, Input, Alert, Ticker, go } from "./ui.js";
import { api, path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";

/** Pure: normalize drift register — Soft invent empty rows forbidden when pack is null. */
export function securityDriftRowsOf(pack) {
  const rows = (pack && (pack.items || pack.rows)) || [];
  return Array.isArray(rows) ? rows : [];
}

/** Pure: security audit rows. */
export function securityAuditRowsOf(pack) {
  const root = pack || {};
  // Live envelope may include command-key drift, MAC drift, or an audit tail.
  if (Array.isArray(root.items) || Array.isArray(root.rows)) {
    return Array.isArray(root.items) ? root.items : root.rows;
  }
  const tail = root.audit_tail;
  if (Array.isArray(tail)) return tail;
  if (tail && Array.isArray(tail.items)) return tail.items;
  const mac = root.mac_drift;
  if (mac && Array.isArray(mac.items)) return mac.items;
  return [];
}

/** Pure: KPI strip from Live packs only. */
export function securityKpisOf(driftRows, auditRows, inhStatus, posture) {
  const inhibited = !!(inhStatus && (
    inhStatus.inhibited === true
    || inhStatus.active === true
    || String(inhStatus.state || "").toLowerCase() === "active"
  ));
  const post = posture && typeof posture === "object" ? posture : {};
  return {
    drift: Array.isArray(driftRows) ? driftRows.length : 0,
    audit: Array.isArray(auditRows) ? auditRows.length : 0,
    inhibitKnown: inhStatus != null,
    inhibited,
    writeSeat: post.rotate_allowed === true || post.inhibit_write_allowed === true,
    armed: post.armed === true,
  };
}

export function SecurityDesk({ toast }) {
  const posture = useApi(path("get__sa_security_status"));
  // Fail-closed while loading — Soft invent open seats deleted.
  const post = posture.loading || !posture.data ? {} : (posture.data || {});
  const product = posture.loading || !posture.data
    ? null
    : (post.product_flag === true ? true : (post.product_flag === false ? false : null));
  const driftOk = post.drift_allowed === true;
  const auditOk = post.audit_allowed === true;
  const rotateOk = post.rotate_allowed === true;
  const inhReadOk = post.inhibit_status_allowed === true;
  const inhWriteOk = post.inhibit_write_allowed === true;
  const detail = post.detail || null;

  const drift = useApi(driftOk ? path("get__sa_security_mac-drift", { query: { limit: 40 } }) : "");
  const audit = useApi(auditOk ? path("get__admin_security_audit", { query: { limit: 40 } }) : "");
  const { run, busy, envelope } = useAction();
  const [deviceId, setDeviceId] = useState("");
  const [confirm, setConfirm] = useState("");
  const [inhTenant, setInhTenant] = useState("");
  const [inhSite, setInhSite] = useState("");
  const [inhReason, setInhReason] = useState("operator emergency stop");
  const [inhStatus, setInhStatus] = useState(null);
  const [inhBusy, setInhBusy] = useState(false);

  const driftRows = useMemo(() => securityDriftRowsOf(drift.data), [drift.data]);
  const auditRows = useMemo(() => securityAuditRowsOf(audit.data), [audit.data]);
  const kpis = useMemo(
    () => securityKpisOf(driftRows, auditRows, inhStatus, post),
    [driftRows, auditRows, inhStatus, post],
  );
  const inhibited = kpis.inhibited;

  const reRead = () => {
    posture.reload && posture.reload();
    drift.reload && drift.reload();
    audit.reload && audit.reload();
    toast && toast("Security posture + buses re-read", "go");
  };

  const rotate = async () => {
    if (!rotateOk) {
      toast && toast("Rotate is not available on this seat.", "warn");
      return;
    }
    const did = deviceId.trim();
    if (!did || confirm.trim() !== did) {
      toast && toast("Type the device serial to confirm", "warn");
      return;
    }
    const out = await run(path("post__admin_security_rotate-cmd-key"), {
      body: { device_id: did },
    });
    if (!out) {
      toast && toast("Rotate refused · nothing was changed", "bad");
      return;
    }
    toast && toast("Command key rotated · " + did, "warn");
    setConfirm("");
    audit.reload && audit.reload();
  };

  const refreshInhibit = async () => {
    if (!inhReadOk) {
      toast && toast("E-stop product unmounted or seat closed", "warn");
      return;
    }
    const tid = inhTenant.trim();
    if (!tid) {
      toast && toast("Organization (tenant_id) required for inhibit status", "warn");
      return;
    }
    setInhBusy(true);
    try {
      const q = new URLSearchParams({ tenant_id: tid });
      if (inhSite.trim()) q.set("site_id", inhSite.trim());
      const out = await api("/sa/resource-fabric/physical-inhibits/status?" + q.toString(), {
        method: "GET",
        timeoutMs: 20000,
      });
      setInhStatus(out);
      toast && toast("Inhibit status", "go");
    } catch (e) {
      setInhStatus(null);
      toast && toast(String((e && e.message) || e || "Inhibit status unavailable"), "warn");
    } finally {
      setInhBusy(false);
    }
  };

  const engageInhibit = async () => {
    if (!inhWriteOk) {
      toast && toast("Emergency stop cannot engage on this seat.", "warn");
      return;
    }
    const tid = inhTenant.trim();
    if (!tid) {
      toast && toast("Organization required", "warn");
      return;
    }
    setInhBusy(true);
    try {
      const key = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID() : ("inh-" + Date.now());
      const out = await api("/sa/resource-fabric/physical-inhibits", {
        method: "POST",
        body: {
          tenant_id: tid,
          site_id: inhSite.trim() || undefined,
          reason: inhReason.trim() || "operator emergency stop",
        },
        headers: { "Idempotency-Key": key },
        timeoutMs: 20000,
      });
      setInhStatus(out);
      toast && toast("Emergency stop engaged", "warn");
    } catch (e) {
      toast && toast(String((e && e.message) || e || "Engage refused · nothing was changed"), "bad");
    } finally {
      setInhBusy(false);
    }
  };

  const clearInhibit = async () => {
    if (!inhWriteOk) {
      toast && toast("Emergency stop cannot clear on this seat.", "warn");
      return;
    }
    const id = String(
      (inhStatus && (inhStatus.inhibit_id || inhStatus.id || (inhStatus.item && inhStatus.item.inhibit_id))) || "",
    ).trim();
    if (!id) {
      toast && toast("Re-read e-stop status first — nothing was changed.", "warn");
      return;
    }
    setInhBusy(true);
    try {
      const key = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID() : ("inhc-" + Date.now());
      const out = await api(
        "/sa/resource-fabric/physical-inhibits/" + encodeURIComponent(id) + "/clear",
        {
          method: "POST",
          body: { reason: "operator clear" },
          headers: { "Idempotency-Key": key },
          timeoutMs: 20000,
        },
      );
      setInhStatus(out);
      toast && toast("Emergency stop cleared", "go");
    } catch (e) {
      toast && toast(String((e && e.message) || e || "Clear refused · nothing was changed"), "bad");
    } finally {
      setInhBusy(false);
    }
  };

  const shell = (title, body) => html`<div className="pc-security-desk pc-security-desk--liquid"
      data-cmc-page="page131-control-security" data-cmc-legacy="page87-control-security"
      data-cmc-residual="page167-security">
    <div className="pc-security-desk__rail">
      <div>
        <p className="eyebrow">Control · Security</p>
        <h2 className="pc-security-desk__title">${title}</h2>
        <p className="note">
          Security status. Rotate and e-stop only succeed after real writes.
        </p>
      </div>
      <div className="btn-row pc-security-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || posture.loading}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Re-read posture</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/control/domains")}>Domains</button>
        <button type="button" className="btn btn--go btn--sm"
          onClick=${() => go("/sa/vault")}>Backup export</button>
      </div>
    </div>
    ${body}
  </div>`;

  // Status SoT first: no product flags, then seat, then Live buses.
  if (!posture.loading && product === false) {
    return shell("Security products unarmed", html`
      <${Card} title="Security posture" meta="status">
        <p className="note">
          product_flag off · detail ${detail || "security_products_disabled"}
          · admin_security ${String(post.admin_security_flag)}
          · mac_acl ${String(post.mac_acl_audit_flag)}
          · physical_inhibit ${String(post.physical_inhibit_flag)}
        </p>
      <//>
      <${Alert} tone="warn" title="Page167 · security buses unmounted"
        say="MAC drift, security audit, rotate, and site e-stop stay empty until product flags arm."/>
    `);
  }

  if (!posture.loading && post.enabled === false && product !== false) {
    return shell("Security seat closed", html`
      <${Card} title="Security posture" meta="status">
        <p className="note">
          detail ${detail || "security_seat_gated"}
          · role ${post.principal_role || "—"}
          · sa_security_admin ${String(!!post.sa_security_admin)}
        </p>
      <//>
      <${Alert} tone="info" title="Page167 seat honesty"
        say="This account cannot open drift, audit, rotate, or e-stop."/>
    `);
  }

  return html`<div className="pc-security-desk pc-security-desk--liquid"
    data-cmc-page="page131-control-security" data-cmc-legacy="page87-control-security"
    data-cmc-residual="page167-security">
    <div className="pc-security-desk__rail">
      <div>
        <p className="eyebrow">Control · Security</p>
        <h2 className="pc-security-desk__title">Live security desk</h2>
        <p className="note">
          MAC drift · security audit · cmd-key rotate · site e-stop.
          Writes only succeed after the server confirms them.
        </p>
      </div>
      <div className="btn-row pc-security-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || posture.loading}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Re-read</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/control/domains")}>Domains</button>
        <button type="button" className="btn btn--go btn--sm"
          onClick=${() => go("/sa/vault")}>Backup export</button>
      </div>
    </div>

    <${Alert} tone="info" title="Live Security"
      say="Security posture from Live. Rotate and emergency stop never report success unless the server accepts the write."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <${Card} title="Security posture" meta="status">
      <div className="btn-row" style=${{ flexWrap: "wrap", gap: "8px 14px" }}>
        <${KV} k="admin_security" v=${post.admin_security_flag ? "on" : "off"}/>
        <${KV} k="mac_acl_audit" v=${post.mac_acl_audit_flag ? "on" : "off"}/>
        <${KV} k="physical_inhibit" v=${post.physical_inhibit_flag ? "on" : "off"}/>
        <${KV} k="drift" v=${driftOk ? "allowed" : "closed"}/>
        <${KV} k="audit" v=${auditOk ? "allowed" : "closed"}/>
        <${KV} k="rotate" v=${rotateOk ? "allowed" : "closed"}/>
        <${KV} k="e-stop" v=${inhWriteOk ? "allowed" : "closed"}/>
      </div>
      ${detail ? html`<p className="note" style=${{ marginTop: 8 }}>detail · ${detail}</p>` : null}
    <//>

    <div className="pc-security-desk__kpis" role="group" aria-label="Security KPIs">
      ${[
        ["MAC drift", kpis.drift, kpis.drift ? "1" : "0", driftOk ? "Live register" : "closed"],
        ["Security audit", kpis.audit, kpis.audit ? "1" : "0", auditOk ? "recent rows" : "closed"],
        [
          "E-stop",
          !inhReadOk ? "off" : (!kpis.inhibitKnown ? "—" : (kpis.inhibited ? "on" : "off")),
          kpis.inhibited ? "1" : "0",
          !inhReadOk ? "unmounted" : (kpis.inhibitKnown ? "Live status" : "not read"),
        ],
        ["Write seat", kpis.writeSeat ? (busy || inhBusy ? "busy" : "idle") : "closed",
          kpis.writeSeat ? "1" : "0",
          kpis.writeSeat ? "POST gates" : "seat"],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="pc-security-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b>${typeof v === "number" ? html`<${Ticker} value=${v}/>` : v}</b>
          <em>${em}</em>
        </div>`)}
    </div>

    <${Card} title="Site emergency stop" meta="status"
        sub=${!post.physical_inhibit_flag
          ? "Product flag off — emergency stop stays sealed."
          : (!inhWriteOk
            ? "Seat closed — sa_security_admin or superadmin required."
            : "Engage or clear with a one-time key. Inhibited state only from Live.")}>
      ${!post.physical_inhibit_flag ? html`
        <${Alert} tone="warn" title="Physical inhibit unmounted"
          say="RESOURCE_FABRIC_PHYSICAL_INHIBIT unarmed. Soft site stop theatre is forbidden."/>`
      : html`
        <${Field} label="Organization (tenant_id)">
          <${Input} icon="user" value=${inhTenant} onChange=${(e) => setInhTenant(e.target.value)} disabled=${!inhWriteOk && !inhReadOk}/>
        <//>
        <${Field} label="Site id (optional)">
          <${Input} icon="chip" value=${inhSite} onChange=${(e) => setInhSite(e.target.value)} disabled=${!inhWriteOk && !inhReadOk}/>
        <//>
        <${Field} label="Reason">
          <${Input} icon="alert" value=${inhReason} onChange=${(e) => setInhReason(e.target.value)} disabled=${!inhWriteOk}/>
        <//>
        <${KV} k="Inhibited now" v=${inhStatus == null ? "—" : (inhibited ? "yes" : "no")}
          tone=${inhibited ? "bad" : "go"}/>
        <div className="btn-row" style=${{ marginTop: 8 }}>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${inhBusy || !inhReadOk} onClick=${refreshInhibit}>
            Re-read status</button>
          <button type="button" className="btn btn--danger btn--sm" disabled=${inhBusy || inhibited || !inhWriteOk} onClick=${engageInhibit}>
            Engage e-stop</button>
          <button type="button" className="btn btn--go btn--sm" disabled=${inhBusy || !inhibited || !inhWriteOk} onClick=${clearInhibit}>
            Clear e-stop</button>
        </div>`}
    <//>

    <${Card} title="MAC drift" meta="status" cls="mt">
      ${!driftOk
        ? html`<p className="note">Drift closed on this seat.
            mac_acl_audit=${String(!!post.mac_acl_audit_flag)} · sa_security_admin=${String(!!post.sa_security_admin)}</p>`
        : drift.loading && !drift.data
          ? html`<p className="note">Reading drift…</p>`
          : drift.error
            ? html`<${Alert} tone="warn" title="MAC drift unavailable"
                say=${String(drift.error)}/>`
            : !driftRows.length
              ? html`<p className="note">No drift rows yet.</p>`
              : driftRows.slice(0, 16).map((r, i) => html`
                  <${KV} key=${r.target || r.device_id || i}
                    k=${String(r.target || r.device_id || "device")}
                    v=${[r.mac_fp, r.reason, r.source].filter(Boolean).join(" · ") || String(r.status || "drift")}/>`)}
    <//>

    <${Card} title="Security audit" meta="status" cls="mt">
      ${!auditOk
        ? html`<p className="note">Audit closed on this seat.
            admin_security=${String(!!post.admin_security_flag)}</p>`
        : audit.loading && !audit.data
          ? html`<p className="note">Reading security audit…</p>`
          : audit.error
            ? html`<${Alert} tone="warn" title="Security audit unavailable"
                say=${String(audit.error)}/>`
            : !auditRows.length
              ? html`<p className="note">No recent security audit rows — Empty.</p>`
              : auditRows.slice(0, 16).map((r, i) => html`
                  <${KV} key=${r.id || i}
                    k=${String(r.action || r.event || "event")}
                    v=${String(r.actor || r.target || "—")}/>`)}
    <//>

    <${Card} title="Rotate command key" meta="status" cls="mt"
        sub="Irreversible from the console. Soft “rotated” without this POST is forbidden.">
      ${!rotateOk ? html`
        <${Alert} tone="info" title="Rotate seat closed"
          say="Requires the security product and a security admin seat. Rotation status only from Live."/>`
      : html`
        <${Field} label="Device serial">
          <${Input} icon="chip" value=${deviceId} onChange=${(e) => setDeviceId(e.target.value)}/>
        <//>
        <${Field} label="Type serial to confirm">
          <${Input} icon="alert" value=${confirm} onChange=${(e) => setConfirm(e.target.value)}/>
        <//>
        <button type="button" className="btn btn--danger btn--sm" disabled=${busy} onClick=${rotate} style=${{ marginTop: 8 }}>
          Rotate cmd key</button>`}
    <//>
  </div>`;
}
